import * as GC from '@mescius/spread-sheets';
import type { CollectedProperty } from '../types';
import {
  LoopMappingProperty,
  MappingProperty,
  Sheet,
} from '../services/api/generated';
import {
  collectLoopRanges,
  getLoopCount,
  getLoopHeaderRange,
  isPropertyInLoop,
  isPropertyInLoopHeader,
  toApiLoopRange,
} from './loop';
import { MAPPING_RESTRICTION, OPTION_CATEGORY_ID } from '../constants';
import { collectProperties } from './property';

/**
 *
 * @param property
 * @returns
 */
function toMappingProperty(property: CollectedProperty): MappingProperty {
  return {
    propertyId: property.propertyId,
    propertyName: property.propertyName,
    propertyKey: property.propertyKey,
    categoryKey: property.categoryKey,
    rowIndex: property.rowIndex,
    colIndex: property.colIndex,
    targetCell: property.targetCell,
  };
}

/**
 *
 * @param sheet
 * @param sheetId
 * @returns
 */
export function buildSheetPayload(
  sheet: GC.Spread.Sheets.Worksheet,
  sheetId: number
): Sheet {
  const loops = collectLoopRanges(sheet);
  const properties = collectProperties(sheet);

  const loopMappingProperties: LoopMappingProperty[] = loops.map((loop) => {
    const collectedProperties = properties.filter(
      (property) =>
        isPropertyInLoop(property, loop) &&
        isPropertyInLoopHeader(property, loop)
    );

    let categoryId = 0;
    let mappingRestriction = '';
    const sameCategoryMappingProperties = collectedProperties.filter(
      (property) =>
        property.mappingRestriction !== MAPPING_RESTRICTION.NOTHING &&
        property.categoryId !== OPTION_CATEGORY_ID
    );

    // 同一ループ内のプロパティのうち、『制限なし』と『オプションカテゴリ』を除けばカテゴリは一意に定まるはず
    if (sameCategoryMappingProperties.length > 0) {
      categoryId = sameCategoryMappingProperties[0].categoryId;
      mappingRestriction = sameCategoryMappingProperties[0].mappingRestriction;
    }

    const mappingProperties = collectedProperties.map(toMappingProperty);
    return {
      loopId: loop.loopId,
      loopCount: getLoopCount(sheet, loop),
      loopRange: toApiLoopRange(sheet, loop),
      mappingEnableCellRange: getLoopHeaderRange(sheet, loop),
      mappingProperties,
      categoryId: categoryId,
      mappingRestriction: mappingRestriction,
    };
  });

  const mappingProperties: MappingProperty[] = properties
    .filter(
      (property) => !loops.some((loop) => isPropertyInLoop(property, loop))
    )
    .map(toMappingProperty);

  return {
    sheetId,
    mappingProperties,
    loopMappingProperties,
  };
}

/**
 *
 * @param spread
 * @returns
 */
export function buildSheetsPayload(spread: GC.Spread.Sheets.Workbook): Sheet[] {
  const result: Sheet[] = [];

  for (let i = 0; i < spread.getSheetCount(); i++) {
    const sheetPayload = buildSheetPayload(spread.getSheet(i), i);
    // 明細範囲かマッピング情報のどちらかがある場合のみレスポンスに追加
    if (
      sheetPayload.loopMappingProperties.length > 0 ||
      sheetPayload.mappingProperties.length > 0
    ) {
      result.push(sheetPayload);
    }
  }

  return result;
}
